/**
 * Leg (selection column) layout for a wagering pool.
 *
 * A pool spans `PoolRaces` consecutive races and pays out `Legs` finishing
 * positions within each of them, so the runner grid renders `Legs * PoolRaces`
 * selection columns. Modifiers override how many legs a race contributes:
 * Box pools every position into one column, KeyWheel needs a key column and a
 * wheel column.
 */

/** Modifier abbreviations as stored in `selectedModifier` */
export const LEG_MODIFIER = {
  STRAIGHT: '',
  BOX: 'BX',
  KEY_BOX: 'KB',
  POWER_BOX: 'PB',
  KEY_WHEEL: 'KW',
  DUTCH: 'DutchCost',
  ABC: 'ABC',
} as const;

/**
 * Legs a single race contributes for the given modifier.
 * @param {number} legs IPool.Legs
 * @param {string} modifier Modifier abbreviation
 * @returns {number} Legs per race, 0 when the modifier is unknown
 */
export const legsPerRace = (legs?: number, modifier?: string): number => {
  switch (modifier) {
    case LEG_MODIFIER.STRAIGHT:
    case LEG_MODIFIER.KEY_BOX:
    case LEG_MODIFIER.POWER_BOX:
    case LEG_MODIFIER.DUTCH:
    case LEG_MODIFIER.ABC:
      return legs || 0;

    case LEG_MODIFIER.BOX:
      return 1;

    case LEG_MODIFIER.KEY_WHEEL:
      return 2;

    default:
      return 0;
  }
};

/**
 * Selection columns to render, numbered from 1.
 * @param {number} legs IPool.Legs
 * @param {number} poolRaces IPool.PoolRaces
 * @param {string} modifier Modifier abbreviation
 * @returns {number[]} Column numbers, e.g. [1, 2, 3] for a straight trifecta
 */
export const betLegRange = (legs?: number, poolRaces?: number, modifier?: string): number[] => {
  const columns = legsPerRace(legs, modifier) * (poolRaces || 1);

  const legRange: number[] = [];
  for (let i = 1; i <= columns; i++) {
    legRange.push(i);
  }

  return legRange;
};
